import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LogoFull } from './logo';
import { BookIcon, GlobeIcon, HomeIcon, MoonIcon, SparkleIcon, SunIcon, UserIcon } from './icons';
import { InstallButton } from './install-button';
import { SearchBar } from './search-bar';
import { useTheme } from '../theme/theme-context';
import { useAuth } from '../hooks/use-auth';
import { useProfileQuery } from '../hooks/use-profile-query';

interface TopNavProps {
  readonly onLogin?: () => void;
  readonly onSignUp?: () => void;
}

export function TopNav({ onLogin, onSignUp }: TopNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { mode, toggle } = useTheme();
  const { user } = useAuth();
  const profile = useProfileQuery({ enabled: !!user });
  const avatarUrl = profile.data?.avatarUrl ?? null;
  const displayName =
    profile.data?.displayName ??
    (user?.user_metadata?.display_name as string | undefined) ??
    user?.email ??
    t('nav.profile');

  const links = user
    ? [
        { key: 'home', label: t('nav.home'), Icon: HomeIcon, path: '/home' },
        { key: 'explore', label: t('nav.explore'), Icon: SparkleIcon, path: '/explore' },
        { key: 'guides', label: t('nav.guides'), Icon: BookIcon, path: '/guides' },
        { key: 'profile', label: t('nav.profile'), Icon: UserIcon, path: '/profile' },
      ]
    : [
        { key: 'home', label: t('nav.home'), Icon: HomeIcon, path: '/home' },
        { key: 'guides', label: t('nav.guides'), Icon: BookIcon, path: '/guides' },
        { key: 'languages', label: t('nav.languages'), Icon: GlobeIcon, path: '/languages' },
      ];

  const activeKey = (() => {
    if (
      location.pathname.startsWith('/home') ||
      location.pathname.startsWith('/article') ||
      location.pathname.startsWith('/tag/') ||
      location.pathname.startsWith('/channel/') ||
      location.pathname.startsWith('/search')
    )
      return 'home';
    if (location.pathname.startsWith('/explore') || location.pathname.startsWith('/topics')) return 'explore';
    if (location.pathname.startsWith('/guides')) return 'guides';
    // /guides/:id sub-route also keeps Guides highlighted (already covered
    // by the startsWith check above).
    if (location.pathname.startsWith('/profile') || location.pathname.startsWith('/saved')) return 'profile';
    if (location.pathname.startsWith('/languages')) return 'languages';
    return null;
  })();

  const initial = (user?.user_metadata?.display_name as string | undefined)?.charAt(0)?.toUpperCase()
    ?? user?.email?.charAt(0)?.toUpperCase()
    ?? 'N';

  const renderLinks = (variant: 'top' | 'bottom') => (
    <div className={variant === 'top' ? 'nw-topnav__links' : 'nw-bottomnav__links'}>
      {links.map(({ key, label, Icon, path }) => (
        <button
          key={key}
          type="button"
          className={
            variant === 'top'
              ? `nw-topnav__link ${activeKey === key ? 'is-active' : ''}`
              : `nw-bottomnav__link ${activeKey === key ? 'is-active' : ''}`
          }
          onClick={() => navigate(path)}
        >
          <Icon size={variant === 'top' ? 15 : 22} stroke={2} />
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <nav className="nw-topnav">
        <button
          type="button"
          className="nw-topnav__brand"
          onClick={() => navigate(user ? '/home' : '/')}
          aria-label={t('nav.a11y.home')}
        >
          <LogoFull size={28} />
        </button>
        {/* Inline pill nav — hidden on mobile via CSS, replaced by the
            fixed bottom-tab-bar rendered as a sibling below. */}
        {renderLinks('top')}
        <div className="nw-topnav__right">
          {user && <SearchBar />}
          <InstallButton />
          <button type="button" className="nw-theme-toggle" onClick={toggle} aria-label={t('nav.a11y.toggleTheme')}>
            {mode === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
          </button>
          {user ? (
            <button
              type="button"
              className="nw-avatar"
              onClick={() => navigate('/profile')}
              aria-label={t('nav.a11y.profile')}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  referrerPolicy="no-referrer"
                />
              ) : (
                initial
              )}
            </button>
          ) : (
            <>
              <button type="button" className="nw-btn nw-btn--ghost" onClick={onLogin}>
                {t('nav.signIn')}
              </button>
              <button type="button" className="nw-btn nw-btn--primary" onClick={onSignUp}>
                {t('common.actions.startReading')}
              </button>
            </>
          )}
        </div>
      </nav>
      {/* Bottom tab bar — sibling of .nw-topnav so its position:fixed
          isn't trapped by the topnav's `backdrop-filter` containing block.
          Hidden on desktop, fully visible on mobile. */}
      <nav className="nw-bottomnav" aria-label={t('nav.a11y.primary')}>
        {renderLinks('bottom')}
      </nav>
    </>
  );
}
